import { ProductCategoryKeys } from '../../../../common/enums/common';
import { TyConfig, useConfig } from '../../../../providers/config-provider';
import { UserProfileProps, useUserProfile } from '../../../../providers/user-provider';
import { IProductDetails, IProductVariant } from '../../../../services/helper/productInterfaces';
import { TyRegionBasic } from '../../../../types';
import { safeGet } from '../../../../utils/objectUtils';
import { getNewTagNumberOfDays, getRxCompatibleRules } from '../../../../utils/configUtils';
import { findDaysDiffOfTwoDate } from '../../../../utils/dateUtils';
import { IRx } from '../../../account/rx/components/interfaces/ICommon';
import { WebEnabled } from '../../../../utils/commerceToolsUtils';
import { getKPRegion } from '../../../../utils/regionUtils';

interface IFrameCuration {
  maxPD: number;
  minPD: number;
}

// These constants will be used to find the localized text for the specific key we defined below
export const Tags = {
  NEW: 'product.tile.tagNew',
  INSTORE: 'product.tile.tagInstore',
  INCOMPATIBLE: 'product.tile.tagIncompatiblelRx',
  CATEGORY_INSTORE: 'in_store_purchase',
};

// This function calculates the highest power for each eye using the formula max(|sph|, |sph + cyl|) while maintaining the sign (+ or -).
// It then determines the overall maximum eye power and retrieves frame curation values (maxPD and minPD) based on this power and the region.
// The user's maximum and minimum PD values are calculated by adding and subtracting these frame curation values from the user's PD.
// Finally, it checks if the variant DBC falls within the user's PD range for both eyes. If not, it marks the item as incompatible.
export const CheckIncompatibleRxTagAvailability = (variant: IProductVariant | IProductDetails) => {
  const { userProfile, liveSessionData } = useUserProfile();
  const userPd = {
    left: Number(liveSessionData?.eg_pd?.distance?.left ?? 0),
    right: Number(liveSessionData?.eg_pd?.distance?.right ?? 0),
  };
  let incompatiblelRxItemsExist = false;
  const activePrescription: IRx =
    safeGet(userProfile, 'prescriptions.active', []).find(
      (prescription: IRx) => prescription?.prescriptionId === userProfile?.selectedEyeglassRxId,
    ) || {};
  const { leftEye, rightEye } = activePrescription;
  const region = userProfile?.region?.toLowerCase() || '';
  // Perform a new calculation to determine dbc. If dbc falls within the user's cosmetic range, it will be considered compatible; otherwise, it will be deemed invalid.
  const variantDbc = variant?.eyeSize + variant?.dbl;

  if (userPd.left > 0 && userPd.right > 0) {
    const leftEyeSph = Number(leftEye?.sph);
    const leftEyeSphCyl = leftEyeSph + Number(leftEye?.cyl);
    const rightEyeSph = Number(rightEye?.sph);
    const rightEyeSphCyl = rightEyeSph + Number(rightEye?.cyl);

    const maxLeftEye = getMaxPower(leftEyeSph, leftEyeSphCyl);
    const maxRightEye = getMaxPower(rightEyeSph, rightEyeSphCyl);
    const maxEyePower = getMaxPower(maxLeftEye, maxRightEye);
    const { maxPD, minPD } = getFrameCuration(maxEyePower, region as TyRegionBasic);

    const userBinocularPd = userPd.left + userPd.right;
    const userBinocularMaxPD = userBinocularPd + maxPD;
    const userBinocularMinPD = userBinocularPd - minPD;

    const isRxCompatible = variantDbc >= userBinocularMinPD && variantDbc <= userBinocularMaxPD;

    if (!isRxCompatible) {
      incompatiblelRxItemsExist = true;
    }
  } else {
    console.log('One or both PD values are missing or not greater than 0.');
  }

  return incompatiblelRxItemsExist;
};

export const checkNewTagAvailability = (
  releaseDate: string,
  type = ProductCategoryKeys.EYEGLASS_FRAMES,
) => {
  const { config } = useConfig();
  let isNewItemsAvailable = false;
  const today = new Date();
  const diffInDays = findDaysDiffOfTwoDate(releaseDate, today);
  const newTagDays = getNewTagNumberOfDays(config, type);
  if (diffInDays && diffInDays <= newTagDays) {
    isNewItemsAvailable = true;
  }
  return isNewItemsAvailable;
};

export const CheckInStoreOnlyAvailability = (variant: IProductVariant) => {
  const { config } = useConfig();
  const kpRegion = (getKPRegion() ? getKPRegion().toLowerCase() : 'scal') as keyof TyConfig;

  if (!config?.[kpRegion]?.commerceTools?.isWebEnabled) {
    return false;
  }
  if (variant?.webEnabled === String(WebEnabled.VISIBLE_NOT_SELLABLE)) {
    return true;
  }
  return false;
  // const masterVariant = (variants || [])?.filter(itm => itm?.variant === 'masterVariant');
  // const InStoreItems =
  //   masterVariant[0]?.categories?.filter(item => item?.key === Tags.CATEGORY_INSTORE) || [];
  // const result = InStoreItems?.length > 0;
  // console.log('result', result);
  // return result;
};

const getMaxPower = (firstPower: number, secondPower: number) =>
  Math.abs(firstPower) > Math.abs(secondPower) ? firstPower : secondPower;

const getFrameCuration = (eyePower: number, region: TyRegionBasic): IFrameCuration => {
  const { config } = useConfig();
  const frameCuration = { maxPD: 0, minPD: 0 };
  const eyePowerRange = Math.abs(eyePower);
  if (Math.sign(eyePower) == -1 && config) {
    const negativeRangeItems = getRxCompatibleRules(config, region, 'negative');
    const findNegativeRange = negativeRangeItems?.find(
      (itm: any) => eyePowerRange >= itm.startRange && eyePowerRange <= itm.endRange,
    );
    if (findNegativeRange) {
      frameCuration.maxPD = findNegativeRange.maxPD;
      frameCuration.minPD = findNegativeRange.minPD;
    }
  } else if (Math.sign(eyePower) == 1 && config) {
    const positiveRangeItems = getRxCompatibleRules(config, region, 'positive');
    const findPositveRange = positiveRangeItems?.find(
      (itm: any) => eyePowerRange >= itm.startRange && eyePowerRange <= itm.endRange,
    );
    if (findPositveRange) {
      frameCuration.maxPD = findPositveRange.maxPD;
      frameCuration.minPD = findPositveRange.minPD;
    }
  }
  return frameCuration;
};
